import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Panel, Pill, fmtDateTime } from "./Kit";

export const WeeklyReport = () => {
  const [reports, setReports] = useState(null);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reports");
      setReports(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch (e) {
      setReports([]);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/reports/generate");
      toast.success("Your AI weekly report is ready.");
      setSelected(data);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Panel
      testId="weekly-report-panel"
      title="AI Weekly Report"
      className="mt-4"
      action={
        <button
          type="button"
          data-testid="generate-report-btn"
          onClick={generate}
          disabled={generating}
          className="inline-flex items-center gap-1.5 rounded-md bg-elv-green px-3.5 py-1.5 text-[11px] font-semibold text-[#04120A] disabled:opacity-60"
        >
          {generating ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Sparkles className="h-3 w-3" />}
          {generating ? "Claude is writing…" : "Generate now"}
        </button>
      }
    >
      <p className="text-[11px] text-elv-dim">
        A plain-English summary of leads, bookings and missed follow-ups, written by Claude Sonnet 4.6.
        Scheduled automatically every Monday at 08:00 in your organization's timezone.
      </p>

      {reports === null ? (
        <p className="mt-4 text-[11px] text-elv-dim">Loading reports…</p>
      ) : reports.length === 0 ? (
        <p data-testid="reports-empty" className="mt-4 text-[11px] text-elv-dim">
          No reports yet. Generate your first one now — it takes about 15 seconds.
        </p>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-[200px_1fr]">
          <div className="grid content-start gap-1.5" data-testid="reports-list">
            {reports.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`report-item-${r.id}`}
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/reports/${r.id}`);
                    setSelected(data);
                  } catch (e) { setSelected(r); }
                }}
                className={`rounded-md border px-3 py-2 text-left text-[11px] transition-colors ${
                  selected?.id === r.id
                    ? "border-elv-green/40 bg-elv-green/[0.07] text-elv-text"
                    : "border-elv-line bg-white/[0.02] text-elv-dim hover:text-elv-text"
                }`}
              >
                <span className="font-mono">{r.week}</span>
                <span className="mt-0.5 block text-[10px] opacity-70">
                  {r.period_start} → {r.period_end}
                </span>
                <Pill tone={r.trigger === "scheduled" ? "green" : "dim"}>{r.trigger}</Pill>
              </button>
            ))}
          </div>
          {selected ? (
            <div data-testid="report-content" className="rounded-md border border-elv-line bg-white/[0.02] p-4">
              <p className="mb-3 font-mono text-[10px] text-elv-dim">
                Week {selected.week} · generated {fmtDateTime(selected.created_at)} · {selected.model}
              </p>
              <div className="whitespace-pre-wrap text-xs leading-relaxed text-elv-text">
                {String(selected.content || "").replace(/\*\*/g, "")}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
};
